<template>
  <!-- 订单创建弹窗 -->
  <OrderFormModal
    v-model:visible="showOrderForm"
    :planId="planDetail?.id"
    @success="loadData"
  />

  <!-- 方案详情弹窗 -->
  <Teleport to="body">
    <div v-if="visible" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="$emit('update:visible', false)"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">{{ planDetail?.name }}</h3>
              <p class="text-sm text-slate-500">方案编号: {{ planDetail?.planNo }}</p>
            </div>
            <button @click="closeModal" class="p-1.5 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="overflow-y-auto flex-1 min-h-0">
            <!-- 状态标签 -->
            <div class="px-6 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-3">
              <span :class="['px-3 py-1 rounded-full text-sm font-medium', getStatusClass(planDetail?.status)]">
                {{ getStatusText(planDetail?.status) }}
              </span>
              <span v-if="planDetail?.needApproval" class="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">待审批</span>
            </div>

            <div class="p-6">
              <!-- 基本信息 -->
              <div class="mb-6">
                <h4 class="text-sm font-medium text-slate-700 mb-3">基本信息</h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div class="bg-slate-50 rounded-lg p-3">
                    <div class="text-xs text-slate-500 mb-1">客户</div>
                    <div class="font-medium text-slate-900">{{ planDetail?.customer?.name || '-' }}</div>
                  </div>
                  <div class="bg-slate-50 rounded-lg p-3 sm:col-span-2">
                    <div class="text-xs text-slate-500 mb-2">引入人</div>
                    <div v-if="hasReferrer" class="flex items-start gap-3">
                      <div class="w-9 h-9 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a4 4 0 00-4-4h-1M9 20H4v-2a4 4 0 014-4h1m0-4a4 4 0 118 0 4 4 0 01-8 0z"/>
                        </svg>
                      </div>
                      <div class="min-w-0 flex-1">
                        <div class="font-medium text-slate-900 truncate">{{ planDetail.referrer.name || '-' }}</div>
                        <div class="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div class="flex items-center gap-1.5 min-w-0 text-xs text-slate-600">
                            <svg class="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h2.28a1 1 0 01.95.68l1.1 3.29a1 1 0 01-.5 1.2l-1.52.76a11.04 11.04 0 005.76 5.76l.76-1.52a1 1 0 011.2-.5l3.29 1.1a1 1 0 01.68.95V19a2 2 0 01-2 2h-1C8.82 21 3 15.18 3 8V5z"/>
                            </svg>
                            <span class="text-slate-400 shrink-0">手机号</span>
                            <span class="text-slate-700 truncate">{{ planDetail.referrer.phone || '-' }}</span>
                          </div>
                          <div class="flex items-center gap-1.5 min-w-0 text-xs text-slate-600">
                            <svg class="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.42-4.03 8-9 8a9.77 9.77 0 01-4-.84L3 20l1.24-3.1A7.4 7.4 0 013 12c0-4.42 4.03-8 9-8s9 3.58 9 8z"/>
                            </svg>
                            <span class="text-slate-400 shrink-0">微信号</span>
                            <span class="text-slate-700 truncate">{{ planDetail.referrer.wechat || '-' }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="flex items-center gap-2 text-sm text-slate-400">
                      <div class="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3M9 7a4 4 0 100 8 4 4 0 000-8zM3 21a6 6 0 0112 0"/>
                        </svg>
                      </div>
                      <span>暂无引入人</span>
                    </div>
                  </div>
                  <div class="bg-slate-50 rounded-lg p-3">
                    <div class="text-xs text-slate-500 mb-1">方案金额</div>
                    <div class="font-medium text-primary-600">¥{{ planDetail?.totalAmount?.toLocaleString() || '0' }}</div>
                  </div>
                  <div class="bg-slate-50 rounded-lg p-3">
                    <div class="text-xs text-slate-500 mb-1">优惠金额</div>
                    <div class="font-medium text-slate-900">¥{{ planDetail?.discount?.toLocaleString() || '0' }}</div>
                  </div>
                  <div class="bg-slate-50 rounded-lg p-3">
                    <div class="text-xs text-slate-500 mb-1">应付金额</div>
                    <div class="font-medium text-primary-600">¥{{ planDetail?.payableAmount?.toLocaleString() || '0' }}</div>
                  </div>
                </div>
              </div>

              <!-- 方案详情 -->
              <div class="mb-6">
                <h4 class="text-sm font-medium text-slate-700 mb-3">方案详情</h4>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="prose prose-sm max-w-none text-slate-600" v-html="planDetail?.detailContent || '-'"></div>
                </div>
              </div>

              <!-- 备注 -->
              <div class="mb-6">
                <h4 class="text-sm font-medium text-slate-700 mb-3">备注</h4>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-600">{{ planDetail?.remark || '-' }}</div>
                </div>
              </div>

              <!-- 订单列表 -->
              <div class="mb-6">
                <div class="flex items-center justify-between mb-3">
                  <h4 class="text-sm font-medium text-slate-700">订单明细</h4>
                  <button
                    v-if="planDetail?.status === 1"
                    @click="openOrderForm"
                    class="px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm flex items-center gap-1.5"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                    </svg>
                    新增订单
                  </button>
                </div>
                <div class="space-y-4">
                  <div v-for="order in planDetail?.orders" :key="order.id" class="border border-slate-200 rounded-lg overflow-hidden">
                    <div class="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                      <div class="flex items-center gap-3">
                        <span class="font-medium text-slate-900">{{ order.productNameSnapshot }}</span>
                        <span class="text-xs text-slate-500">{{ order.productCodeSnapshot }}</span>
                        <span :class="[
                          'px-2 py-0.5 rounded text-xs font-medium',
                          order.orderType === 'ONCE' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                        ]">
                          {{ order.orderType === 'ONCE' ? '一次性服务' : `分期服务（${order.periodCount}期）` }}
                        </span>
                      </div>
                      <span :class="['px-2 py-0.5 rounded text-xs font-medium', getOrderStatusClass(order.status)]">
                        {{ getOrderStatusText(order.status) }}
                      </span>
                    </div>
                    <div class="p-4">
                      <div class="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                        <div>
                          <div class="text-xs text-slate-500 mb-1">订单编号</div>
                          <div class="text-sm text-slate-900">{{ order.orderNo }}</div>
                        </div>
                        <div>
                          <div class="text-xs text-slate-500 mb-1">服务周期</div>
                          <div class="text-sm text-slate-900">{{ order.serviceStart }} ~ {{ order.serviceEnd }}</div>
                        </div>
                        <div>
                          <div class="text-xs text-slate-500 mb-1">订单金额</div>
                          <div class="text-sm font-medium text-primary-600">¥{{ order.amount?.toLocaleString() || '0' }}</div>
                        </div>
                        <div>
                          <div class="text-xs text-slate-500 mb-1">落地执行人员</div>
                          <div class="text-sm text-slate-900">{{ order.executor?.displayName || order.executor?.username || '-' }}</div>
                        </div>
                        <div>
                          <div class="text-xs text-slate-500 mb-1">备注</div>
                          <div class="text-sm text-slate-600">{{ order.remark || '-' }}</div>
                        </div>
                      </div>

                      <!-- 账单列表 -->
                      <div v-if="order.billings && order.billings.length > 0" class="border-t border-slate-200 pt-4">
                        <div class="flex items-center gap-2 mb-2">
                          <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                          </svg>
                          <span class="text-sm font-medium text-slate-700">账单明细</span>
                        </div>
                        <div class="data-table-shell">
                          <div class="data-table-scroll">
                            <table class="data-table text-sm">
                          <thead class="data-table-head">
                            <tr>
                              <th class="data-table-th">期次</th>
                              <th class="px-3 py-2 text-right text-xs font-medium text-slate-500">应收金额</th>
                              <th class="data-table-th">应收日期</th>
                              <th class="px-3 py-2 text-center text-xs font-medium text-slate-500">状态</th>
                              <th class="px-3 py-2 text-right text-xs font-medium text-slate-500">已收</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="billing in order.billings" :key="billing.id" class="data-table-row">
                              <td class="data-table-td text-slate-900">第{{ billing.periodIndex }}期</td>
                              <td class="data-table-td text-right font-medium text-slate-900">¥{{ billing.amount?.toLocaleString() }}</td>
                              <td class="data-table-td text-slate-600">{{ billing.dueDate }}</td>
                              <td class="data-table-td text-center">
                                <span :class="['status-badge', getBillingStatusClass(billing.status)]">
                                  {{ getBillingStatusText(billing.status) }}
                                </span>
                              </td>
                              <td class="data-table-td text-right text-slate-600">¥{{ billing.paidAmount?.toLocaleString() || '0' }}</td>
                            </tr>
                          </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 轨迹记录 -->
              <div>
                <h4 class="text-sm font-medium text-slate-700 mb-3">操作记录</h4>
                <div class="space-y-3">
                  <div v-for="trace in traceList" :key="trace.id" class="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <div class="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </div>
                    <div class="flex-1">
                      <div class="flex items-center gap-2">
                        <span class="font-medium text-slate-900">{{ trace.title || trace.action }}</span>
                        <span v-if="trace.traceType === 'STATUS'" class="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">状态变更</span>
                        <span v-else class="text-xs px-2 py-0.5 bg-slate-100 text-slate-700 rounded">备注</span>
                      </div>
                      <div v-if="trace.content" class="text-sm text-slate-600 mt-1">{{ trace.content }}</div>
                      <div class="text-xs text-slate-400 mt-1">{{ trace.operatorName }} · {{ formatDateTime(trace.createTime) }}</div>
                    </div>
                  </div>
                  <div v-if="traceList.length === 0" class="text-center py-4 text-slate-500 text-sm">
                    暂无操作记录
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="px-6 py-4 border-t border-slate-200 flex justify-between gap-3 shrink-0">
            <button
              v-if="planDetail?.status === 1"
              @click="handleCancelPlan"
              class="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              取消方案
            </button>
            <div v-else></div>
            <button @click="closeModal" class="px-5 py-2.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">关闭</button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getPlanDetail, cancelPlan } from '@/api/plan'
import { getTraceList } from '@/api/trace'
import OrderFormModal from '@/components/OrderFormModal.vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  planId: {
    type: [String, Number],
    default: null
  },
  planData: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:visible', 'success'])

const planDetail = ref(null)
const traceList = ref([])
const hasReferrer = computed(() => Boolean(planDetail.value?.referrer?.id || planDetail.value?.referrer?.name))

// 订单创建相关
const showOrderForm = ref(false)

// 状态映射
const statusMap = {
  0: { text: '草稿', class: 'bg-slate-100 text-slate-600' },
  1: { text: '执行中', class: 'bg-blue-100 text-blue-700' },
  2: { text: '已完成', class: 'bg-green-100 text-green-700' },
  3: { text: '已取消', class: 'bg-red-100 text-red-700' },
  4: { text: '待审批', class: 'bg-amber-100 text-amber-700' }
}

const orderStatusMap = {
  0: { text: '待开始', class: 'bg-slate-100 text-slate-600' },
  1: { text: '服务中', class: 'bg-blue-100 text-blue-700' },
  2: { text: '已完成', class: 'bg-green-100 text-green-700' },
  3: { text: '已取消', class: 'bg-red-100 text-red-700' }
}

const billingStatusMap = {
  0: { text: '待收', class: 'bg-amber-100 text-amber-700' },
  1: { text: '已收', class: 'bg-green-100 text-green-700' },
  2: { text: '部分收', class: 'bg-blue-100 text-blue-700' },
  3: { text: '已核销', class: 'bg-purple-100 text-purple-700' }
}

const getStatusClass = (status) => statusMap[status]?.class || 'bg-slate-100 text-slate-600'
const getStatusText = (status) => statusMap[status]?.text || '未知'
const getOrderStatusClass = (status) => orderStatusMap[status]?.class || 'bg-slate-100 text-slate-600'
const getOrderStatusText = (status) => orderStatusMap[status]?.text || '未知'
const getBillingStatusClass = (status) => billingStatusMap[status]?.class || 'bg-slate-100 text-slate-600'
const getBillingStatusText = (status) => billingStatusMap[status]?.text || '未知'

const formatDateTime = (date) => {
  if (!date) return '-'
  return new Date(date).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const loadData = async () => {
  // 检查是否已有完整数据（包含 customer 和 orders）
  const hasFullData = props.planData?.customer && props.planData?.orders?.length > 0

  if (hasFullData) {
    // 如果传递了完整数据，直接使用
    planDetail.value = props.planData
  } else {
    // 需要获取完整数据
    const id = props.planData?.id || props.planId
    if (!id) {
      console.warn('没有方案ID，无法加载详情')
      return
    }
    try {
      planDetail.value = await getPlanDetail(id)
    } catch (error) {
      console.error('加载方案详情失败', error)
      return
    }
  }

  // 加载轨迹记录
  if (planDetail.value?.id) {
    try {
      traceList.value = await getTraceList('PLAN', planDetail.value.id)
    } catch (error) {
      console.error('加载轨迹记录失败', error)
    }
  }
}

const closeModal = () => {
  emit('update:visible', false)
}

// 打开订单表单
const openOrderForm = () => {
  showOrderForm.value = true
}

// 取消方案
const handleCancelPlan = async () => {
  try {
    await ElMessageBox.confirm(
      '确定要取消此方案吗？取消后将删除方案下的所有订单和账单，此操作不可恢复。',
      '取消方案',
      {
        confirmButtonText: '确定取消',
        cancelButtonText: '我再想想',
        type: 'warning',
        confirmButtonClass: 'el-button--danger'
      }
    )

    await cancelPlan(planDetail.value.id)
    ElMessage.success('方案已取消')

    // 关闭弹窗并通知父组件刷新
    emit('update:visible', false)
    emit('success')
  } catch (error) {
    if (error === 'cancel') {
      // 用户点击了取消按钮，不做任何操作
      return
    }
    ElMessage.error(error.message || '取消方案失败')
  }
}

watch(() => props.visible, (val) => {
  if (val) {
    loadData()
  } else {
    planDetail.value = null
    traceList.value = []
  }
}, { immediate: true })
</script>
